// frontend/src/features/resi/ResiPage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchRitiriResi,
  oggiIso,
  ritiroResoMancabile,
  type Prenotazione,
} from "@/features/ritiri/api";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { PageHeader } from "@/shared/ui/PageHeader";
import { CampoCerca, ChipFiltro } from "@/shared/ui/ChipFiltro";
import { type StatoTone } from "@/features/ordini/components/statoToni";
import { prettifyApiError } from "@/shared/api";
import { usePermessi } from "@/shared/auth/permessi";
import { useReturns, type ReturnKind, type ReturnRecord } from "./api";
import { EmettiEtichettaDialog } from "./components/EmettiEtichettaDialog";
import { PrenotaRitiroResoDialog } from "./components/PrenotaRitiroResoDialog";
import { STATUS_BADGE } from "./components/PraticaBadge";
import { PraticaCard } from "./components/PraticaCard";
import { openReturnLabel } from "./labelDownload";

/** Il tono dei chip Tutti / Resi / Sostituzioni: quello dei badge di tipo. */
const TONO_TAB: Record<"" | ReturnKind, StatoTone> = {
  "": "annullato",
  reso: "sost-as",
  sostituzione: "sost-fornitore",
};

/** I chip di stato, nell'ordine in cui una pratica li attraversa. */
const STATI = ["richiesta", "etichetta_pronta", "in_transito", "consegnato", "chiuso", "annullato"];

function IconInbox({ className }: { className?: string }) {
  return (
    <svg
      className={
        className ??
        "fill-none stroke-current stroke-[1.4] [stroke-linecap:round] [stroke-linejoin:round]"
      }
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

export function ResiPage() {
  const [kind, setKind] = useState<"" | ReturnKind>("");
  const [stato, setStato] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  // Ricerca full-text (ordine/cliente): ``debouncedSearch`` è ciò che arriva a
  // ``useReturns`` (→ backend ``q=``), così non lanciamo una query per ogni tasto.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Pratica per cui si sta emettendo l'etichetta (passo 2), o null.
  const [daEmettere, setDaEmettere] = useState<ReturnRecord | null>(null);

  const { items, total, counts, ordini, isLoading, error, refresh } = useReturns({
    kind: kind || undefined,
    status: stato || undefined,
    q: debouncedSearch || undefined,
  });

  // Ritiro dal cliente dei resi con l'etichetta pronta: l'ultima prenotazione di
  // ognuno, in qualunque stato. ``versioneRitiri`` cresce a ogni prenotazione
  // fatta da qui: gli id dei resi restano gli stessi, e senza l'effect non
  // richiederebbe i ritiri aggiornati.
  const [ritiriResi, setRitiriResi] = useState<Record<string, Prenotazione>>({});
  // Per quale ``idsResi`` la mappa sopra è valida: finché non combacia con
  // quello corrente nessuna riga mostra un chip — «mai chiesto» è un'affermazione
  // (link neutro), non va detta prima di aver davvero controllato.
  const [ritiriCaricatiPer, setRitiriCaricatiPer] = useState<string | null>(null);
  const [ritiroPer, setRitiroPer] = useState<ReturnRecord | null>(null);
  const [versioneRitiri, setVersioneRitiri] = useState(0);
  const puoRitiri = usePermessi().puoScrivere("spedizioni");
  const idsResi = items
    .filter((r) => r.kind === "reso" && r.status === "etichetta_pronta")
    .map((r) => r.id)
    .join(",");
  useEffect(() => {
    if (!idsResi) {
      setRitiriResi({});
      setRitiriCaricatiPer(idsResi);
      return;
    }
    // Ignora una risposta fuori ordine: una fetch precedente più lenta non
    // deve sovrascrivere la mappa con dati più vecchi di quelli già a schermo.
    let attivo = true;
    fetchRitiriResi(idsResi.split(",").map(Number))
      .then((mappa) => {
        if (!attivo) return;
        setRitiriResi(mappa);
        setRitiriCaricatiPer(idsResi);
      })
      .catch((e: unknown) => {
        if (attivo) setActionError(prettifyApiError(e));
      });
    return () => {
      attivo = false;
    };
  }, [idsResi, versioneRitiri]);
  // Chiude il dialog di prenotazione e riallinea la mappa: dopo un rifiuto SDA
  // l'ultimo tentativo del reso è cambiato anche se l'operatore chiude con
  // «Chiudi» invece di riuscire (``onPrenotato``) — le due strade ricaricano
  // allo stesso modo.
  const chiudiRitiro = useCallback(() => {
    setRitiroPer(null);
    setVersioneRitiri((v) => v + 1);
    void refresh();
  }, [refresh]);

  const tabs = useMemo(
    () =>
      [
        ["", "Tutti"],
        ["reso", "Resi"],
        ["sostituzione", "Sostituzioni"],
      ] as const,
    [],
  );

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Resi e sostituzioni"
        subtitle="Spedizioni di reso e sostituzione · etichette SDA, BRT all'estero"
        meta={
          <span className="inline-flex h-[30px] items-center gap-[6px] rounded-full border border-line-2 bg-card px-3">
            <span aria-hidden className="h-[5px] w-[5px] rounded-full bg-accent" />
            <span className="text-[12.5px] font-bold tabular-nums text-ink">
              {isLoading ? "···" : total}
            </span>
            <span className="text-[12.5px] text-ink">pratiche</span>
          </span>
        }
      />

      <div className="mb-4 flex items-center gap-2 max-md:flex-col max-md:items-stretch">
        <div className="flex flex-wrap items-center gap-1 max-md:gap-2">
          {tabs.map(([value, label]) => (
            <ChipFiltro
              key={value}
              tono={TONO_TAB[value]}
              attivo={kind === value}
              onClick={() => setKind(value)}
            >
              {label}
            </ChipFiltro>
          ))}
        </div>
        <CampoCerca
          value={search}
          onChange={setSearch}
          placeholder="Cerca ordine, cliente…"
          ariaLabel="Cerca resi e sostituzioni"
          className="ml-auto w-[300px] max-md:ml-0 max-md:w-full"
        />
      </div>

      <ErrorBanner message={error} className="mb-3" />
      <ErrorBanner message={actionError} className="mb-3" />

      <div
        role="group"
        aria-label="Filtra per stato"
        className="mb-4 flex flex-wrap items-center gap-1.5 max-md:-mx-3 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-3 max-md:pb-1"
      >
        <ChipFiltro tono="annullato" attivo={stato === ""} onClick={() => setStato("")}>
          Tutti gli stati
        </ChipFiltro>
        {STATI.map((s) => {
          const b = STATUS_BADGE[s];
          if (!b) return null;
          return (
            <ChipFiltro
              key={s}
              tono={b.tono}
              attivo={stato === s}
              onClick={() => setStato(stato === s ? "" : s)}
              conteggio={counts[s] ?? 0}
              // Le pratiche senza etichetta sono lavoro da fare: il chip lo dice.
              evidenzia={s === "richiesta" && (counts[s] ?? 0) > 0}
            >
              {b.label}
            </ChipFiltro>
          );
        })}
      </div>

      {!isLoading && items.length === 0 ? (
        <Card className="flex flex-col items-center py-12 text-center">
          <span
            aria-hidden
            className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-ink-2"
          >
            <IconInbox className="h-6 w-6 fill-none stroke-current stroke-[1.4] [stroke-linecap:round] [stroke-linejoin:round]" />
          </span>
          <h3 className="mb-1.5 font-display text-[16px] font-bold text-ink">
            Nessun reso o sostituzione
          </h3>
          <p className="max-w-[260px] text-[12.5px] leading-relaxed text-ink-2">
            Creane uno dalla pagina Ordini o Messaggi.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-[10px]">
          {items.map((r) => (
            <PraticaCard
              key={r.id}
              record={r}
              ordine={ordini?.[r.order_reference]}
              // Ritorna la promise: la riga ci appende il proprio stato busy.
              onOpenLabel={() => {
                setActionError(null);
                return openReturnLabel(r).catch((e) => setActionError(prettifyApiError(e)));
              }}
              onEmettiEtichetta={() => setDaEmettere(r)}
              ritiro={
                ritiriCaricatiPer === idsResi ? (ritiriResi[String(r.id)] ?? null) : undefined
              }
              onPrenotaRitiro={puoRitiri ? () => setRitiroPer(r) : undefined}
            />
          ))}
        </div>
      )}

      {daEmettere && (
        <EmettiEtichettaDialog
          record={daEmettere}
          onEmessa={() => {
            setDaEmettere(null);
            void refresh();
          }}
          onClose={() => setDaEmettere(null)}
        />
      )}
      {ritiroPer && (
        <PrenotaRitiroResoDialog
          record={ritiroPer}
          mancato={(() => {
            const p = ritiriResi[String(ritiroPer.id)];
            return p && ritiroResoMancabile(p, oggiIso()) ? p : null;
          })()}
          onChiudi={chiudiRitiro}
          onPrenotato={chiudiRitiro}
        />
      )}
    </main>
  );
}
